import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Workflow {
  id: string
  name: string
  description: string
  status: string
  created_by: string
  created_at: string
  updated_at: string
  trigger: {
    type: string
    config: Record<string, unknown>
  }
  conditions: Array<{
    id: string
    field: string
    operator: string
    value: unknown
  }>
  actions: Array<{
    id: string
    type: string
    config: Record<string, unknown>
  }>
  execution_count: number
  last_execution: string | null
  success_rate: number
  tags: string[]
}

interface Execution {
  id: string
  workflow_id: string
  workflow_name: string
  status: string
  started_at: string
  completed_at: string | null
  duration_ms: number
  trigger_type: string
  steps: Array<{
    action_id: string
    action_type: string
    status: string
    output?: Record<string, unknown>
    error?: string
  }>
  error?: string
}

interface Template {
  id: string
  name: string
  description: string
  category: string
  icon: string
  use_count: number
}

interface Statistics {
  workflows: {
    total: number
    active: number
    paused: number
    draft: number
  }
  executions: {
    total: number
    successful: number
    failed: number
    success_rate: number
  }
  performance: {
    average_duration_ms: number
    by_trigger_type: Record<string, number>
  }
  recent_activity: {
    executions_last_7_days: number
    workflows_modified: number
  }
}

const API_BASE = '/api'

export default function WorkflowAutomationPage() {
  const [workflows, setWorkflows] = useState<Workflow[]>([])
  const [executions, setExecutions] = useState<Execution[]>([])
  const [templates, setTemplates] = useState<Template[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'workflows' | 'executions' | 'templates'>('workflows')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showExecutionModal, setShowExecutionModal] = useState(false)
  const [selectedExecution, setSelectedExecution] = useState<Execution | null>(null)

  useEffect(() => {
    fetchData()
  }, [statusFilter])

  const fetchData = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)

      const [workflowsRes, executionsRes, templatesRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/workflows?${params}`),
        fetch(`${API_BASE}/workflows/executions`),
        fetch(`${API_BASE}/workflows/templates`),
        fetch(`${API_BASE}/workflows/statistics`)
      ])

      if (workflowsRes.ok) {
        const data = await workflowsRes.json()
        setWorkflows(data.workflows || [])
      }
      if (executionsRes.ok) {
        const data = await executionsRes.json()
        setExecutions(data.executions || [])
      }
      if (templatesRes.ok) {
        const data = await templatesRes.json()
        setTemplates(data.templates || [])
      }
      if (statsRes.ok) {
        const data = await statsRes.json()
        setStatistics(data)
      }
    } catch (error) {
      console.error('Failed to fetch workflow data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExecuteWorkflow = async (workflowId: string) => {
    try {
      const response = await fetch(`${API_BASE}/workflows/${workflowId}/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: {} })
      })
      if (response.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Failed to execute workflow:', error)
    }
  }

  const handleToggleStatus = async (workflow: Workflow) => {
    const endpoint = workflow.status === 'active' ? 'pause' : 'activate'
    try {
      const response = await fetch(`${API_BASE}/workflows/${workflow.id}/${endpoint}`, {
        method: 'POST'
      })
      if (response.ok) {
        fetchData()
      }
    } catch (error) {
      console.error('Failed to toggle workflow status:', error)
    }
  }

  const handleDeleteWorkflow = async (workflowId: string) => {
    if (!confirm('Are you sure you want to delete this workflow?')) return
    try {
      const response = await fetch(`${API_BASE}/workflows/${workflowId}`, {
        method: 'DELETE'
      })
      if (response.ok) {
        fetchData()
        setSelectedWorkflow(null)
      }
    } catch (error) {
      console.error('Failed to delete workflow:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'paused': return 'bg-yellow-100 text-yellow-800'
      case 'draft': return 'bg-gray-100 text-gray-800'
      case 'completed': return 'bg-blue-100 text-blue-800'
      case 'failed': return 'bg-red-100 text-red-800'
      case 'running': return 'bg-purple-100 text-purple-800'
      case 'cancelled': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTriggerIcon = (triggerType: string) => {
    switch (triggerType) {
      case 'schedule': return '(clock)'
      case 'event': return '(zap)'
      case 'manual': return '(hand)'
      case 'webhook': return '(link)'
      case 'data_change': return '(database)'
      case 'threshold': return '(alert-triangle)'
      default: return '(play)'
    }
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never'
    return new Date(dateStr).toLocaleString()
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Workflow Automation</h1>
          <p className="text-gray-600 mt-2">Create and manage automated workflows with triggers, conditions, and actions</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Total Workflows</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{statistics.workflows.total}</div>
              <div className="text-sm text-green-600 mt-2">{statistics.workflows.active} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Total Executions</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{statistics.executions.total}</div>
              <div className="text-sm text-blue-600 mt-2">{statistics.recent_activity.executions_last_7_days} this week</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Success Rate</div>
              <div className="text-3xl font-bold text-green-600 mt-1">{statistics.executions.success_rate.toFixed(1)}%</div>
              <div className="text-sm text-gray-500 mt-2">{statistics.executions.failed} failed</div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-500">Avg Duration</div>
              <div className="text-3xl font-bold text-gray-900 mt-1">{formatDuration(statistics.performance.average_duration_ms)}</div>
              <div className="text-sm text-gray-500 mt-2">per execution</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('workflows')}
            className={`px-6 py-3 font-medium ${activeTab === 'workflows' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Workflows ({workflows.length})
          </button>
          <button
            onClick={() => setActiveTab('executions')}
            className={`px-6 py-3 font-medium ${activeTab === 'executions' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Executions ({executions.length})
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-6 py-3 font-medium ${activeTab === 'templates' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Templates ({templates.length})
          </button>
        </div>

        {/* Filters and Actions */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex gap-4">
            {activeTab === 'workflows' && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="draft">Draft</option>
              </select>
            )}
          </div>
          {activeTab === 'workflows' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              + Create Workflow
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {/* Workflows Tab */}
            {activeTab === 'workflows' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                  {workflows.map((workflow) => (
                    <div
                      key={workflow.id}
                      onClick={() => setSelectedWorkflow(workflow)}
                      className={`bg-white rounded-lg shadow p-6 cursor-pointer transition-all hover:shadow-md ${selectedWorkflow?.id === workflow.id ? 'ring-2 ring-blue-500' : ''}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{workflow.name}</h3>
                          <p className="text-sm text-gray-500 mt-1">{workflow.description}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(workflow.status)}`}>
                          {workflow.status}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                        <span className="flex items-center gap-1">
                          {getTriggerIcon(workflow.trigger.type)} {workflow.trigger.type}
                        </span>
                        <span>{workflow.actions.length} actions</span>
                        <span>{workflow.execution_count} runs</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          {workflow.tags.map((tag) => (
                            <span key={tag} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleStatus(workflow) }}
                            className={`px-3 py-1 rounded text-sm ${workflow.status === 'active' ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}
                          >
                            {workflow.status === 'active' ? 'Pause' : 'Activate'}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleExecuteWorkflow(workflow.id) }}
                            className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                          >
                            Run
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  {workflows.length === 0 && (
                    <div className="text-center py-12 bg-white rounded-lg shadow">
                      <p className="text-gray-500">No workflows found</p>
                      <button
                        onClick={() => setShowCreateModal(true)}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      >
                        Create your first workflow
                      </button>
                    </div>
                  )}
                </div>

                {/* Workflow Detail Panel */}
                <div className="bg-white rounded-lg shadow p-6">
                  {selectedWorkflow ? (
                    <>
                      <div className="flex justify-between items-start mb-6">
                        <h3 className="text-lg font-semibold">{selectedWorkflow.name}</h3>
                        <button
                          onClick={() => handleDeleteWorkflow(selectedWorkflow.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="text-sm text-gray-500">Trigger</label>
                          <div className="mt-1 p-3 bg-gray-50 rounded">
                            <div className="font-medium">{selectedWorkflow.trigger.type}</div>
                            <pre className="text-xs text-gray-600 mt-1 overflow-x-auto">
                              {JSON.stringify(selectedWorkflow.trigger.config, null, 2)}
                            </pre>
                          </div>
                        </div>

                        {selectedWorkflow.conditions.length > 0 && (
                          <div>
                            <label className="text-sm text-gray-500">Conditions</label>
                            <div className="mt-1 space-y-2">
                              {selectedWorkflow.conditions.map((cond) => (
                                <div key={cond.id} className="p-2 bg-yellow-50 rounded text-sm">
                                  {cond.field} {cond.operator} {JSON.stringify(cond.value)}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        <div>
                          <label className="text-sm text-gray-500">Actions ({selectedWorkflow.actions.length})</label>
                          <div className="mt-1 space-y-2">
                            {selectedWorkflow.actions.map((action, idx) => (
                              <div key={action.id} className="p-3 bg-blue-50 rounded">
                                <div className="flex items-center gap-2">
                                  <span className="w-6 h-6 bg-blue-200 rounded-full flex items-center justify-center text-xs">{idx + 1}</span>
                                  <span className="font-medium text-sm">{action.type}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="border-t pt-4">
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <label className="text-gray-500">Success Rate</label>
                              <div className="font-medium text-green-600">{selectedWorkflow.success_rate.toFixed(1)}%</div>
                            </div>
                            <div>
                              <label className="text-gray-500">Total Runs</label>
                              <div className="font-medium">{selectedWorkflow.execution_count}</div>
                            </div>
                            <div className="col-span-2">
                              <label className="text-gray-500">Last Execution</label>
                              <div className="font-medium">{formatDate(selectedWorkflow.last_execution)}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-500">
                      <p>Select a workflow to view details</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Executions Tab */}
            {activeTab === 'executions' && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Workflow</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trigger</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {executions.map((execution) => (
                      <tr
                        key={execution.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => { setSelectedExecution(execution); setShowExecutionModal(true) }}
                      >
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{execution.workflow_name}</div>
                          <div className="text-sm text-gray-500">{execution.id}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(execution.status)}`}>
                            {execution.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {getTriggerIcon(execution.trigger_type)} {execution.trigger_type}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDuration(execution.duration_ms)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(execution.started_at)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {execution.steps.filter(s => s.status === 'completed').length}/{execution.steps.length} completed
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {executions.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No executions found
                  </div>
                )}
              </div>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {templates.map((template) => (
                  <div key={template.id} className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-xl">
                        {template.icon === 'file-text' && '(doc)'}
                        {template.icon === 'bell' && '(bell)'}
                        {template.icon === 'database' && '(db)'}
                        {template.icon === 'zap' && '(zap)'}
                        {template.icon === 'link' && '(link)'}
                      </div>
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded">
                        {template.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
                    <p className="text-sm text-gray-600 mb-4">{template.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Used {template.use_count} times</span>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                        Use Template
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Create Workflow Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Create New Workflow</h2>
                  <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600">
                    X
                  </button>
                </div>
              </div>
              <div className="p-6">
                <p className="text-gray-600 mb-6">
                  Choose a template to get started quickly, or create a custom workflow from scratch.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  {templates.slice(0, 4).map((template) => (
                    <button
                      key={template.id}
                      className="p-4 border rounded-lg text-left hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      onClick={() => {
                        setShowCreateModal(false)
                        alert(`Create workflow from template: ${template.name}`)
                      }}
                    >
                      <div className="font-medium">{template.name}</div>
                      <div className="text-sm text-gray-500 mt-1">{template.description}</div>
                    </button>
                  ))}
                </div>
                <div className="mt-6 pt-6 border-t">
                  <button className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors">
                    + Create Custom Workflow
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Execution Detail Modal */}
        {showExecutionModal && selectedExecution && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold">Execution Details</h2>
                  <button onClick={() => setShowExecutionModal(false)} className="text-gray-400 hover:text-gray-600">
                    X
                  </button>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-semibold">{selectedExecution.workflow_name}</h3>
                    <p className="text-sm text-gray-500">{selectedExecution.id}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedExecution.status)}`}>
                    {selectedExecution.status}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <div className="text-sm text-gray-500">Trigger</div>
                    <div className="font-medium">{selectedExecution.trigger_type}</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <div className="text-sm text-gray-500">Duration</div>
                    <div className="font-medium">{formatDuration(selectedExecution.duration_ms)}</div>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded">
                    <div className="text-sm text-gray-500">Steps</div>
                    <div className="font-medium">{selectedExecution.steps.length}</div>
                  </div>
                </div>

                {selectedExecution.error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="text-sm font-medium text-red-800">Error</div>
                    <div className="text-sm text-red-600 mt-1">{selectedExecution.error}</div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-4">Execution Steps</h4>
                  <div className="space-y-3">
                    {selectedExecution.steps.map((step, idx) => (
                      <div key={step.action_id} className={`p-4 rounded-lg border ${step.status === 'completed' ? 'bg-green-50 border-green-200' : step.status === 'failed' ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${step.status === 'completed' ? 'bg-green-200 text-green-800' : step.status === 'failed' ? 'bg-red-200 text-red-800' : 'bg-gray-200 text-gray-800'}`}>
                              {idx + 1}
                            </span>
                            <span className="font-medium">{step.action_type}</span>
                          </div>
                          <span className={`text-sm ${step.status === 'completed' ? 'text-green-600' : step.status === 'failed' ? 'text-red-600' : 'text-gray-600'}`}>
                            {step.status}
                          </span>
                        </div>
                        {step.error && (
                          <div className="mt-2 text-sm text-red-600">{step.error}</div>
                        )}
                        {step.output && (
                          <pre className="mt-2 text-xs text-gray-600 bg-white p-2 rounded overflow-x-auto">
                            {JSON.stringify(step.output, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
